/**
 * Esprima Studios — Stone Options Config
 * -------------------------------------------
 * Source of truth for per-SKU stone-swap requests, sourced from live
 * Stuller.com research (checked 2026-07-14) cross-referenced against each
 * piece's own Shopify product description.
 *
 * SCOPE — read this before adding to the list:
 * The Bezel-Set Solitaire Necklace (SKU family under Stuller SERIES 61134)
 * is NOT included here. That product already ships all 27 of Stuller's
 * stone/metal/carat combinations (the full birthstone chart, lab-grown and
 * natural diamond, even moissanite) as native Shopify variants — the
 * built-in variant picker already handles stone selection for it. Adding
 * a duplicate request module on top of a fully-stocked variant picker
 * would just confuse buyers.
 *
 * UPDATED 2026-07-15 (source-of-truth chain audit): three of the original five
 * entries — the Quarter/Half/Full Carat Round Bezel-Set Solitaire Slide Pendants
 * (formerly V01-PR-PD-003/004/005) — were REMOVED from this file. When Presence
 * was rebuilt from the full Stuller variant feed, Stuller item 2000048's entire
 * tier family (Petite through Full Carat, 8 Shopify products) picked up BOTH
 * Natural and Lab-Grown Diamond as native purchasable variants, same as the
 * Bezel-Set Solitaire Necklace. The gap this module existed to cover — natural
 * diamond promised in copy but not yet a real variant — no longer exists for
 * any of them. Re-adding a request module on top of a fully-stocked variant
 * picker would just confuse buyers (see the existing note on SERIES 61134 below).
 *
 * The Domed Bezel-Set Stud Earrings — Petite entry (V01-PR-ER-001, Stuller
 * mounting 29919 family) is KEPT below but flagged ORPHANED: that placeholder
 * SKU no longer appears anywhere in the live catalog as of 2026-07-15, and no
 * product with a matching Stuller item number was found either. It's not clear
 * whether this piece was renamed, merged into a different listing, or simply
 * never re-published — needs manual verification before relying on this entry
 * for anything. The underlying Stuller research is preserved in case it's
 * still needed once the product's current identity is confirmed.
 *
 * The two pieces below were originally different: they're single-variant products
 * whose own Shopify description already promises something not yet a
 * purchasable variant — e.g. "lab-grown diamond by default, natural
 * diamond available on request." That's the gap this module fills.
 *
 * The domed stud earrings entry (now orphaned, see above) confirmed exactly
 * two stone origins available at its mounting/size on Stuller: Natural
 * Diamond and Lab-Grown Diamond — no colored-stone option exists at that
 * mounting.
 *
 * The Round Two-Stone Earring Mounting is different: it's sold
 * UNSET on Stuller and its own "Set Stones" configurator (opened live,
 * 2026-07-14) genuinely offers Diamond, CZ, and 20 gemstone types at both
 * stone positions — see STONE_23563_OPTIONS below. That list is real
 * Stuller catalog data for this exact mounting, not a guess.
 *
 * Keyed by the Shopify VARIANT SKU. Grade values (clarity/color) are
 * Stuller's typical grade for that origin at this mounting family — they
 * were not re-verified per exact size on every SKU, so they're shown to
 * the buyer as "typical," not guaranteed, and get reconfirmed by staff
 * before the Stuller order is placed (same proofing discipline as the
 * engraving module). Non-diamond gemstone requests get a price-quote note
 * instead of a grade, since Stuller pricing varies far more by stone type
 * than diamond clarity/color does.
 */

// Full stone-type catalog for Stuller mounting 23563 (Round Two-Stone
// Earring Mounting), read directly from its own "Set Stones" configurator.
// Shared by both stone positions on that mounting since Stuller offers the
// same catalog at each position.
var STONE_23563_OPTIONS = [
  { value: "lab-grown-diamond", label: "Lab-Grown Diamond (current default)", grade: "SI1-SI2 clarity, G-H color (typical)" },
  { value: "natural-diamond", label: "Natural Diamond", grade: "I1 clarity, G-H color (typical)", priceNote: "Natural diamond typically costs more than lab-grown at the same size — we'll confirm exact pricing before placing the Stuller order." },
  { value: "alexandrite", label: "Alexandrite", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "amethyst", label: "Amethyst", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "aquamarine", label: "Aquamarine", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "citrine", label: "Citrine", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "cz", label: "Cubic Zirconia (CZ)", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "emerald", label: "Emerald", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "garnet", label: "Garnet", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "iolite", label: "Iolite", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "jade", label: "Jade", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "moissanite", label: "Moissanite", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "opal", label: "Opal", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "peridot", label: "Peridot", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "quartz", label: "Quartz", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "ruby", label: "Ruby", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "sapphire", label: "Sapphire", priceNote: "Available in Natural, Lab-Grown, or Imitation on this mounting — tell us which you'd like in the notes below. Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "spinel", label: "Spinel", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "tanzanite", label: "Tanzanite", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "topaz", label: "Topaz", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "tourmaline", label: "Tourmaline", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "turquoise", label: "Turquoise", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." },
  { value: "zircon", label: "Zircon", priceNote: "Pricing and lead time vary by quality — we'll send you a quote before placing the Stuller order." }
];

window.EsprimaStoneConfig = {

  // ORPHANED as of 2026-07-15 — no live product found with this SKU or a matching
  // Stuller item number. Confirmed via source-of-truth chain audit (catalog-wide
  // SKU search). Do not delete outright until manually verified against Admin >
  // Products; the mounting research below is still accurate for item 29919 if the
  // product resurfaces under a new SKU.
  "V01-PR-ER-001": {

    stullerMountingItem: "29919 family (Round/Domed Bezel-Set Stud Earring mountings)",
    productName: "Domed Bezel-Set Stud Earrings — Petite",
    locations: [
      {
        id: "pair",
        label: "Both Earrings (sold as a pair)",
        shape: "Round",
        size: "~2.5 mm each",
        carat: ".06 CTW total",
        defaultOrigin: "Lab-Grown Diamond",
        defaultGrade: "SI1-SI2 clarity, G-H color (typical)",
        origins: [
          { value: "lab-grown", label: "Lab-Grown Diamond (current default)", grade: "SI1-SI2 clarity, G-H color (typical)" },
          { value: "natural", label: "Natural Diamond", grade: "I1 clarity, G-H color (typical)", priceNote: "Natural diamond typically costs more than lab-grown at the same size — we'll confirm exact pricing before placing the Stuller order." }
        ]
      }
    ],
    notes: "Confirmed live on Stuller.com against the Round/Domed Bezel-Set Stud Earring mounting family (item 29919 and siblings), 2026-07-14. This family is Diamond-only (Natural/Lab-Grown) at Stuller — no colored-stone option exists for this mounting."
  },

  // Round Two-Stone Earring Mounting
  // Stuller mounting: 23563 (14K White Gold 3mm & 3.4mm Round Two-Stone Earring Mounting) — sold unset.
  // Stone list below is the exact catalog returned by Stuller's own "Set Stones" configurator on this
  // mounting (Customization Options > Diamonds/Gemstones > Set Stones > Stone Types), checked live 2026-07-14.
  // Diamond, CZ, and 20 gemstone types are all genuinely orderable against this mounting — this is not a
  // guess or an extrapolation from a category facet list.
  "V01-TD-ER-001": {

    stullerMountingItem: "23563",
    productName: "Round Two-Stone Earring Mounting",
    locations: [
      {
        id: "large",
        label: "3.4 mm Stone",
        shape: "Round",
        size: "3.4 mm",
        carat: null,
        inputType: "select",
        defaultOrigin: "Lab-Grown Diamond",
        defaultGrade: "SI1-SI2 clarity, G-H color (typical)",
        origins: STONE_23563_OPTIONS
      },
      {
        id: "small",
        label: "3 mm Stone",
        shape: "Round",
        size: "3 mm",
        carat: null,
        inputType: "select",
        defaultOrigin: "Lab-Grown Diamond",
        defaultGrade: "SI1-SI2 clarity, G-H color (typical)",
        origins: STONE_23563_OPTIONS
      }
    ],
    notes: "Confirmed live on Stuller.com against mounting 23563's own \"Set Stones\" configurator, 2026-07-14 — both positions draw from the same 22-stone-type catalog (Diamond, CZ, and 20 gemstones). Each colored gemstone also has its own Imitation/Lab-Grown/Natural sub-tiers on Stuller (confirmed by opening Sapphire's options as a sample) and its own per-stone-type pricing that this file does not attempt to model — those get quoted by staff once a request comes in, same as the natural-diamond price note below."
  }

  // NOT INCLUDED: Bezel-Set Solitaire Necklace (SERIES 61134) — already has all
  // 27 stone/metal/carat combinations live as native Shopify variants. Use the
  // theme's normal variant picker for that product; this module would be
  // redundant there.
};
